import L, { LatLngLiteral } from "leaflet";
import "leaflet/dist/images/layers-2x.png";
import "leaflet/dist/images/layers.png";
import "leaflet/dist/images/marker-icon-2x.png";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import "leaflet/dist/leaflet.css";
import React, { Component } from "react";
import { LatLng, LeafletContext, Viewport } from "react-leaflet";
import MapComponentView from "./MapComponent.view";
import {
  // AnimationType,
  // INFINITE_ANIMATION_ITERATIONS,
  GpsActiveMessage,
  MapEventMessage,
  MapLayer,
  MapMarker,
  MapShape,
  WebViewLeafletEvents,
  WebviewLeafletMessagePayload,
} from "./models";
import "./styles/markerAnimations.css";
import "./styles/markers.css";
import mockMapLayers from "./testData/mockMapLayers";
// import mockMapMarkers from "./testData/mockMapMarkers";
// import mockMapShapes from "./testData/mockMapShapes";

export const SHOW_DEBUG_INFORMATION = false;
const ENABLE_BROWSER_TESTING = true;
/** Nombre de lignes de débogage gardées (affichées seulement avec `debugMap`). */
const DEBUG_MESSAGES_KEPT = 200;
/**
 * 2026-10-10 : ce que cette page sait faire, dit à l'application dans `MAP_READY`.
 * `gpsActive` : le repère du guidage est animé par la page (voir `applyGpsActive`).
 * `headingUp` (2026-10-12) : la page sait tourner la carte dans le sens de la marche (champ
 * `headingUp` du message `gpsActive`, voir `applyHeadingUp`).
 */
const PAGE_FEATURES = { gpsActive: true, headingUp: true };
/** Durée du glissement du repère entre deux positions : bornes, en millisecondes. */
const GPS_SLIDE_MIN_MS = 200;
const GPS_SLIDE_MAX_MS = 3000;
/** Première position, ou « Recentrer » : la carte rejoint le repère en ce temps. */
const GPS_RECENTER_S = 0.5;
/** Prolongation du mouvement : bornes de la durée demandée par l'application (ms). */
const GPS_EXTRAPOLATE_MAX_MS = 5000;
/** Vitesse maximale admise pour la prolongation (m/s), contre une position aberrante. */
const GPS_MAX_SPEED_MPS = 60;
/**
 * Une position reçue DERRIÈRE le repère de moins de cette distance (en mètres) ne le fait pas
 * reculer : le relevé est en retard sur la prolongation, ou le véhicule s'est arrêté pendant
 * elle. 30 m : l'avance prise pendant une prolongation d'une seconde et demie à 50 km/h, avec
 * de la marge ; un vrai recul (marche arrière, demi-tour) de plus de 30 m est suivi.
 */
const GPS_BACKWARD_HOLD_M = 30;
/**
 * Véhicule à l'arrêt d'après le GPS (vitesse annoncée inférieure à 0,5 m/s) : une position à
 * moins de cette distance (en mètres) du repère ne le fait pas bouger (bruit du GPS à l'arrêt).
 * Petit à dessein : Android annonce une vitesse nulle quand il ne la connaît pas, et un véhicule
 * qui roule à plus de 4 m/s doit alors avancer quand même, position après position.
 */
const GPS_STOPPED_HOLD_M = 4;
/**
 * 2026-10-12 : écart (en degrés) entre la direction suivie par le repère et le cap annoncé par
 * l'application au-delà duquel le véhicule est tenu pour avoir tourné : la règle « position
 * derrière le repère » ne joue pas (voir `applyGpsPosition`).
 */
const GPS_TURNED_DEG = 45;
/**
 * 2026-10-12 : une position reçue n'est « derrière le repère » que si sa direction s'écarte de
 * plus de cet angle (en degrés) de la direction suivie, c'est-à-dire si elle est dans l'axe,
 * derrière, à 45° près. Avant : 90° (tout le demi-plan arrière), ce qui prenait pour un relevé
 * en retard toute position sur le côté d'un repère qui avait dépassé un virage.
 */
const GPS_BEHIND_MIN_DEG = 135;
/**
 * Les événements de déplacement et de zoom de la carte (`onMove`, `onMoveEnd`, `onZoom`) sont
 * envoyés à l'application au plus une fois par ce délai. Pendant un glissement de la carte,
 * Leaflet les lève à chaque image ; l'application ne s'en sert pas.
 */
const MOVE_EVENTS_MIN_INTERVAL_MS = 250;
/**
 * 2026-10-12, carte dans le sens de la marche. Vitesse angulaire maximale de la carte, en
 * degrés par seconde : un cap qui saute (GPS bruité, demi-tour) ne la fait pas tourner d'un coup.
 */
const GPS_ROTATE_MAX_DEG_S = 90;
/**
 * Un glissement plus court que cette distance (en mètres) ne change pas l'angle de la carte :
 * à l'arrêt ou presque, le cap reçu ne vaut rien, la carte garde l'angle qu'elle avait.
 */
const GPS_ROTATE_MIN_MOVE_M = 1;
/**
 * Délai entre la fin d'un geste qui ne coupe pas le suivi (pincement) et la reprise de la
 * rotation (ms).
 */
const GPS_ROTATE_RESUME_MS = 400;
/**
 * Après un geste qui coupe le suivi dans l'application (appui sur le fond de carte, glissé), la
 * carte reste nord en haut en attendant que l'application l'annonce (`follow: false`). Ce
 * délai (ms) n'est qu'un filet : si l'annonce n'arrive jamais, la rotation reprend après lui.
 * Long à dessein : l'aller-retour avec l'application peut prendre plusieurs dixièmes de
 * seconde sur un téléphone lent, et une rotation reprise trop tôt se redresserait aussitôt.
 */
const GPS_ROTATE_FOLLOW_WAIT_MS = 3000;
/** Un geste tenu pour fini depuis ce délai (ms) relâche la carte même si un zoom semble en cours. */
const GPS_ROTATE_STUCK_MS = 2000;
/** Déplacement du doigt (pixels) à partir duquel Leaflet commence un glissé (`clickTolerance`). */
const GPS_ROTATE_DRAG_PX = 3;
/**
 * Écouteurs de gestes de la rotation : en phase de capture sur un parent de la carte, ils
 * s'exécutent avant ceux de Leaflet (posés sur la carte) ; ils ne bloquent rien.
 */
const ROTATE_LISTEN = { capture: true, passive: true };

/** Écart signé entre deux angles en degrés, dans [-180, 180[ (plus court chemin). */
const angleDelta = (from: number, to: number): number =>
  ((((to - from) % 360) + 540) % 360) - 180;

/** État du repère animé du guidage (hors de `state` : aucun rendu React par image). */
interface GpsLive {
  marker: L.Marker;
  /** L'image tournée selon le cap. */
  img: HTMLElement | null;
  from: LatLngLiteral;
  to: LatLngLiteral;
  bearingFrom: number;
  bearingTo: number;
  /** Début et durée du glissement en cours. */
  start: number;
  duration: number;
  frame: number | null;
  follow: boolean;
  /** Heure d'arrivée de la dernière position (donne la durée du glissement suivant). */
  lastFixAt: number;
  icon: string;
  size: [number, number];
  /** 2026-10-11 : prolongation du mouvement après la fin du glissement (ms, 0 = jamais). */
  extrapolateMs: number;
  /** Vitesse du dernier glissement (m/s) et direction géographique (degrés). */
  speedMps: number;
  heading: number;
  /** Vrai dès qu'un glissement a donné une direction (avant, `heading` n'est que le cap reçu). */
  headingKnown: boolean;
  /** La caméra a-t-elle reçu son déplacement de prolongation pour ce glissement ? */
  panExtrapolated: boolean;
  /**
   * 2026-10-12 : un glissement ou une prolongation du repère sont-ils en cours ? La boucle
   * d'images sert aussi à la rotation de la carte ; le repère n'y est posé que si c'est vrai,
   * donc exactement aux mêmes images qu'avant.
   */
  animating: boolean;
  /** Ce mouvement donne-t-il un cap fiable à la carte (assez long, ou véhicule qui roule) ? */
  steer: boolean;
  /** L'application demande la carte dans le sens de la marche (`headingUp`). */
  headingUp: boolean;
  /** Cap que la carte vise ; gardé tel quel à l'arrêt. */
  rotateTarget: number;
  /** Suivi vu par la rotation au message précédent (détecte « Recentrer »). */
  rotateFollow: boolean;
}

/** Interpolation d'un angle en degrés par le plus court chemin. */
const lerpAngle = (from: number, to: number, t: number): number => {
  let delta = ((to - from) % 360 + 540) % 360 - 180;
  return (from + delta * t + 360) % 360;
};

/** Distance en mètres entre deux points. */
const distanceM = (a: LatLngLiteral, b: LatLngLiteral): number =>
  L.latLng(a.lat, a.lng).distanceTo(L.latLng(b.lat, b.lng));

/** Direction géographique de `a` vers `b`, en degrés (0 = nord). */
const bearingDeg = (a: LatLngLiteral, b: LatLngLiteral): number => {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLng = toRad(b.lng - a.lng);
  const y = Math.sin(dLng) * Math.cos(toRad(b.lat));
  const x =
    Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) -
    Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

/** Point à `meters` de `from` dans la direction `bearing` (approximation plane, courte distance). */
const movePoint = (from: LatLngLiteral, bearing: number, meters: number): LatLngLiteral => {
  const rad = (bearing * Math.PI) / 180;
  const dLat = (meters * Math.cos(rad)) / 111320;
  const dLng = (meters * Math.sin(rad)) / (111320 * Math.cos((from.lat * Math.PI) / 180));
  return { lat: from.lat + dLat, lng: from.lng + dLng };
};

interface State {
  debugMessages: string[];
  isFromNative: boolean;
  isMobile: boolean;
  mapCenterPosition: LatLngLiteral;
  mapLayers: MapLayer[];
  mapMarkers: MapMarker[];
  mapShapes: MapShape[];
  ownPositionMarker: MapMarker;
  mapRef: any;
  zoom: number;
  className?: string | null;
  contextValue?: LeafletContext | null;
  container?: HTMLDivElement | null;
  viewport?: Viewport | null;
  debugMap?: boolean
  /** 2026-10-12 : carte agrandie en carré, le temps d'un guidage dans le sens de la marche. */
  rotorSquare: boolean;
}

export default class MapComponent extends Component<{}, State> {
  private gpsMarkerLayer: any = null;
  /** 2026-10-10 : repère du guidage animé par la page (message `gpsActive`). */
  private gpsLive: GpsLive | null = null;
  /**
   * 2026-10-12 : rotation de la carte dans le sens de la marche (voir `applyHeadingUp`).
   * `mapAngle` : cap placé en haut de l'écran, en degrés (0 = nord en haut) ; le bloc de la
   * carte est tourné de l'opposé. `rotateOn` : carte carrée et écouteurs de gestes posés.
   * `rotateHold` : un geste tient la carte nord en haut. `rotatePending` : appui commencé sur
   * un repère, qui ne redresse la carte que s'il devient un glissé. `rotateCutsFollow` : le
   * geste en cours coupe le suivi dans l'application (glissé, appui sur le fond de carte).
   */
  private mapAngle = 0;
  private rotateOn = false;
  private rotor: HTMLElement | null = null;
  private rotateContainer: HTMLElement | null = null;
  private rotateHold = false;
  private rotateDown = false;
  private rotateUpAt = 0;
  private rotateCutsFollow = false;
  private rotatePending: { x: number; y: number } | null = null;
  private rotateResumeAt = 0;
  private rotateTimer: number | null = null;
  private rotateFrameAt = 0;
  private unmounting = false;
  /** Heure du dernier envoi de chaque événement de déplacement (voir `onMapEvent`). */
  private lastMoveEventAt: { [event: string]: number } = {};
  /**
   * Zoom demandé par l'application (message `zoom`) : celui auquel « Centrer »
   * (`resetMapState`) revient, et le plancher du zoom quand un nouveau centre arrive.
   */
  private appZoom = 6;
  /**
   * 2026-10-14 : heure (`Date.now()`) à laquelle finit le glissement de caméra lancé par
   * « Recentrer » (`gpsRecenter`) ; 0 s'il n'y en a pas. Une position gardée sur place pendant
   * ce glissement (arrêt, ou derrière le repère) arrête la caméra (`map.stop()`, lot G) : elle
   * est alors relancée vers le repère au lieu de rester en route. Remis à 0 dès qu'un geste
   * déplace la carte (`movestart` de Leaflet) ou que le suivi est coupé.
   */
  private gpsRecenterUntil = 0;
  constructor(props: {}) {
    super(props);
    this.state = {
      debugMessages: ["test"],
      isFromNative: false,
      isMobile: null,
      mapCenterPosition: { lat: 36.56, lng: -76.17 },
      mapLayers: [],
      mapMarkers: [],
      mapShapes: [],
      mapRef: null,
      ownPositionMarker: null,
      zoom: 6,
      debugMap: false,
      rotorSquare: false,
    };
  }

  componentDidMount = () => {
    let DefaultIcon = L.icon({
      iconUrl: icon,
      shadowUrl: iconShadow,
    });
    L.Marker.prototype.options.icon = DefaultIcon;

    this.addEventListeners();
    this.sendMessage({
      msg: WebViewLeafletEvents.MAP_COMPONENT_MOUNTED,
    });
    if (ENABLE_BROWSER_TESTING) {
      this.loadMockData();
    }

    (window as any).updateGpsActiveMarker = (lat: number, lng: number, bearing: number) => {
      if (!this.state.mapRef || !this.state.mapRef.leafletElement) return;
      const map = this.state.mapRef.leafletElement;

      if (!this.gpsMarkerLayer) {
        const customIcon = L.icon({
          iconUrl: 'https://api.fleetlize.fr/uploads/gps-car-marker.png',
          iconSize: [32, 32],
          className: 'gps-animated-marker'
        });
        
        this.gpsMarkerLayer = L.marker([lat, lng], { 
          icon: customIcon,
          rotationAngle: bearing,
          rotationOrigin: 'center center',
          zIndexOffset: 1000
        } as any).addTo(map);
      } 
      else {
        this.gpsMarkerLayer.setLatLng([lat, lng]);
        if (this.gpsMarkerLayer.setRotationAngle) {
          this.gpsMarkerLayer.setRotationAngle(bearing);
        }
      }
    };

    (window as any).removeGpsActiveMarker = () => {
      if (this.gpsMarkerLayer) {
        this.gpsMarkerLayer.remove();
        this.gpsMarkerLayer = null;
      }
    };
  };

  componentDidUpdate = (prevProps: any, prevState: State) => {
    if (this.rotateOn) {
      // Le contenu d'une infobulle ouverte a pu changer de taille avec ce rendu.
      this.refreshPopupOrigin();
    }
    const { mapRef } = this.state;
    if (mapRef && !prevState.mapRef) {
      mapRef.current?.leafletElement.invalidateSize();
      (window as any).leafletMap = mapRef.leafletElement;
      // 2026-10-12 : une infobulle ouverte reste droite quand la carte tourne.
      mapRef.leafletElement.on("popupopen", this.onPopupOpen);
      this.sendMessage({
        msg: WebViewLeafletEvents.MAP_READY,
        features: PAGE_FEATURES,
      });
    }
  };

  componentWillUnmount = () => {
    this.unmounting = true;
    this.removeGpsActive();
  };

  // 2026-10-09 : la liste ne garde que les 200 dernières lignes. Elle grandissait sans fin.
  private addDebugMessage = (msg: any) => {
    const line = typeof msg === "object" ? JSON.stringify(msg, null, 4) : msg;
    this.setState({
      debugMessages: [...this.state.debugMessages, line].slice(-DEBUG_MESSAGES_KEPT),
    });
  };

  private addEventListeners = () => {
    if (document) {
      document.addEventListener("message", this.handleMessage);
      this.addDebugMessage("set document listeners");
      this.sendMessage({
        msg: WebViewLeafletEvents.DOCUMENT_EVENT_LISTENER_ADDED,
      });
    }
    if (window) {
      window.addEventListener("message", this.handleMessage);
      this.addDebugMessage("setting Window");
      this.sendMessage({
        msg: WebViewLeafletEvents.WINDOW_EVENT_LISTENER_ADDED,
      });
    }
    if (!document && !window) {
      this.sendMessage({
        error: WebViewLeafletEvents.UNABLE_TO_ADD_EVENT_LISTENER,
      });
      return;
    }
  };

  private handleMessage = (event: any & { data: State }) => {
    // 2026-10-09 : un message n'est recopié dans la liste de débogage que si son affichage est
    // demandé. Avant, chaque message reçu y restait pour toujours, mis en forme (un itinéraire
    // entier à chaque fois pendant un guidage) : la mémoire de la page enflait jusqu'à son arrêt
    // par le téléphone. Chaque message coûtait aussi deux rendus de plus.
    if (SHOW_DEBUG_INFORMATION || this.state.debugMap) {
      this.addDebugMessage(event.data);
    }
    try {
      if (event.data && typeof event.data.zoom === "number") {
        this.appZoom = event.data.zoom;
      }
      if (
        event.data &&
        Object.prototype.hasOwnProperty.call(event.data, "gpsActive")
      ) {
        // 2026-10-10 : le repère du guidage ne passe pas par l'état React (aucun rendu).
        this.applyGpsActive(event.data.gpsActive);
        return;
      }
      if (event.data.mapCenterPosition) {
        const zoomLevel = event.data.zoomLevel || undefined;
        this.state.mapRef.leafletElement.flyTo(
          [event.data.mapCenterPosition.lat, event.data.mapCenterPosition.lng],
          zoomLevel
        );
      }
      if (event.data.event === 'resetMapState') {
        const { mapCenterPosition } = this.state
        this.state.mapRef.leafletElement.flyTo([
          mapCenterPosition.lat,
          mapCenterPosition.lng,
        ], this.appZoom);
      } else {
        const next: any = { ...this.state, ...event.data };
        const keptZoom = this.zoomForNewCenter(event.data);
        if (keptZoom !== null) {
          next.zoom = keptZoom;
        }
        this.setState(next);
      }
      //  if( event.event === 'resetMapState'){
      //    console.log('event' , )
      //  } else { 
      //  }
    } catch (error) {
      this.addDebugMessage({ error: JSON.stringify(error) });
    }
  };

  /**
   * 2026-10-09 : zoom à donner à la carte quand l'application envoie un NOUVEAU centre
   * (traceur sélectionné, suivi d'un guidage ou d'une lecture d'historique). `null` : ne pas
   * toucher au zoom.
   *
   * La carte est replacée au centre reçu et au zoom gardé ici. Ce zoom valait toujours celui de
   * l'application (17) : dès qu'un centre arrivait, un zoom plus proche choisi au doigt était
   * perdu, la carte revenait à 17. Règle maintenant : le zoom de l'application reste le
   * plancher (on arrive toujours au moins à 17 sur un traceur choisi de loin), mais un zoom plus
   * proche choisi au doigt est gardé. Un zoom demandé avec le centre (`zoomLevel`) passe devant.
   */
  private zoomForNewCenter = (data: any): number | null => {
    const center = data && data.mapCenterPosition;
    if (!center) {
      return null;
    }
    const current = this.state.mapCenterPosition;
    if (current && current.lat === center.lat && current.lng === center.lng) {
      // Même centre : la carte n'est pas replacée, son zoom ne doit pas bouger non plus.
      return null;
    }
    if (typeof data.zoomLevel === "number") {
      return data.zoomLevel;
    }
    const map = this.state.mapRef && this.state.mapRef.leafletElement;
    const shown = map ? map.getZoom() : this.appZoom;
    return Math.max(this.appZoom, shown);
  };

  protected sendMessage = (message: MapEventMessage) => {
    // @ts-ignore
    if (window.ReactNativeWebView) {
      // @ts-ignore
      window.ReactNativeWebView.postMessage(JSON.stringify(message));
      console.log("sendMessage  ", JSON.stringify(message));
    }
  };

  private loadMockData = () => {
    this.addDebugMessage("loading mock data");
    this.setState({
      mapLayers: mockMapLayers,
      // mapMarkers: mockMapMarkers,
      // mapShapes: mockMapShapes,
      // ownPositionMarker: {
      //   id: "Own Position",
      //   position: { lat: 36.56, lng: -76.17 },
      //   icon: "❤️",
      //   size: [32, 32],
      //   // animation: {
      //   //   duration: 1,
      //   //   delay: 0,
      //   //   iterationCount: INFINITE_ANIMATION_ITERATIONS,
      //   //   type: AnimationType.BOUNCE,
      //   // },
      // },
    });
  };

  /**
   * 2026-10-10 : repère du guidage animé par la page.
   *
   * Avant, l'application faisait glisser elle-même la position du repère (une soixantaine de
   * pas par seconde, puis dix depuis le 09/10) et renvoyait à chaque pas le repère et un
   * nouveau centre : la page était redessinée à chaque pas, et la caméra « volait » vers chaque
   * position intermédiaire. Le repère avançait par sauts.
   *
   * Maintenant l'application envoie UNE position par relevé GPS. La page fait glisser le repère
   * de la position affichée à la nouvelle, à vitesse constante, en autant de temps qu'il s'est
   * écoulé depuis le relevé précédent (bornes `GPS_SLIDE_MIN_MS` et `GPS_SLIDE_MAX_MS`), à la
   * cadence d'affichage. Si `follow`, la carte glisse en même temps, par l'animation de
   * déplacement de Leaflet, de même durée et linéaire : le repère reste au centre. Le zoom choisi
   * au doigt est gardé ; en dessous du zoom de l'application (`appZoom`), la carte y revient,
   * comme pour un centre reçu par `mapCenterPosition`.
   */
  private applyGpsActive = (data: GpsActiveMessage | null) => {
    this.applyGpsPosition(data);
    this.applyHeadingUp(data);
  };

  /**
   * Le repère et la caméra : la logique du lot G (2026-10-11), plus la règle du virage du
   * 2026-10-12 (`turned`) et ce que la rotation doit savoir du mouvement (`animating`, `steer`,
   * `rotateTarget`).
   */
  private applyGpsPosition = (data: GpsActiveMessage | null) => {
    const map = this.state.mapRef && this.state.mapRef.leafletElement;
    if (!map) {
      // Page pas encore prête : l'application renvoie la position après `MAP_READY`.
      return;
    }
    // Coordonnées lues en nombres (une chaîne « 48.85 » venue du serveur est acceptée) ;
    // une valeur absente ou illisible retire le repère plutôt que de le figer sur NaN.
    const lat = data ? Number(data.lat) : NaN;
    const lng = data ? Number(data.lng) : NaN;
    if (!data || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      this.removeGpsActive();
      return;
    }
    const now = Date.now();
    const target: LatLngLiteral = { lat, lng };
    const follow = data.follow !== false;
    const live = this.gpsLive;
    const bearingGiven = data.bearing === undefined || data.bearing === null ? NaN : Number(data.bearing);
    const bearing = Number.isFinite(bearingGiven) ? bearingGiven : live ? live.bearingTo : 0;
    const icon = data.icon || (live ? live.icon : "");
    const size: [number, number] =
      Array.isArray(data.size) && data.size.length === 2
        ? [Number(data.size[0]), Number(data.size[1])]
        : live
        ? live.size
        : [32, 32];
    const extrapolateMs = Math.max(
      0,
      Math.min(GPS_EXTRAPOLATE_MAX_MS, Number(data.extrapolateMs) || 0)
    );
    // Vitesse annoncée par l'application (GPS, lissée) ; `null` si absente ou illisible.
    const givenSpeed = Number(data.speedMps);
    const speedGiven = Number.isFinite(givenSpeed) && givenSpeed >= 0 ? Math.min(GPS_MAX_SPEED_MPS, givenSpeed) : null;
    // Cadence attendue des positions (téléphone : 1 s) ; 0 ou absente = durée mesurée (traceur).
    const cadenceMs = Math.max(0, Math.min(GPS_SLIDE_MAX_MS, Number(data.cadenceMs) || 0));

    if (!live || live.icon !== icon || live.size[0] !== size[0] || live.size[1] !== size[1]) {
      // Première position (ou nouvelle icône) : le repère apparaît sur place, sans glissement.
      this.removeGpsMarker();
      map.on("zoomend", this.gpsResumeFollow);
      map.on("movestart", this.gpsRecenterCancel);
      const marker = this.createGpsMarker(target, bearing, icon, size).addTo(map);
      const element = marker.getElement();
      this.gpsLive = {
        marker,
        img: element ? (element.querySelector("img") as HTMLElement | null) : null,
        from: target,
        to: target,
        bearingFrom: bearing,
        bearingTo: bearing,
        start: now,
        duration: 0,
        frame: null,
        follow,
        lastFixAt: now,
        icon,
        size,
        extrapolateMs,
        speedMps: 0,
        heading: bearing,
        headingKnown: false,
        panExtrapolated: true,
        animating: false,
        steer: false,
        headingUp: false,
        rotateTarget: bearing,
        rotateFollow: follow,
      };
      if (follow) {
        // Départ du guidage : la carte se pose sur le véhicule au zoom de l'application
        // (17), comme l'application le faisait avant ; sans animation, pour ne pas être
        // ignoré par Leaflet si un zoom animé est en cours.
        map.setView(target, this.appZoom, { animate: false });
      }
      return;
    }

    const shown = this.gpsShown(now);
    const followTurnedOn = follow && !live.follow;
    // 2026-10-14 : le glissement de caméra de « Recentrer » est-il encore en cours ?
    const recentering = follow && !followTurnedOn && now < this.gpsRecenterUntil;
    if (!follow && live.follow) {
      // Suivi coupé (carte touchée) : la caméra s'arrête là où elle est, au lieu de finir
      // son glissement vers la position visée.
      map.stop();
      this.gpsRecenterUntil = 0;
    }
    live.follow = follow;
    live.extrapolateMs = extrapolateMs;
    // Les règles ci-dessous (répétition, arrêt, position derrière) ne valent qu'avec le
    // téléphone (`cadenceMs` donné) : avec un traceur, toute position reçue est suivie,
    // exactement comme le 10/10.
    const liveRules = cadenceMs > 0;

    if (live.to.lat === target.lat && live.to.lng === target.lng) {
      // Même position : seul `follow` a pu changer (« Recentrer »), ou le GPS a répété son
      // dernier relevé.
      if (liveRules && speedGiven !== null && speedGiven >= 0.5) {
        // Répétition alors que le GPS dit que l'on roule (relevé livré deux fois, ou second
        // message de l'application pour le même relevé) : rien à apprendre, le glissement ou
        // la prolongation en cours continuent.
        if (followTurnedOn) {
          this.gpsRecenter(map, live, now);
        }
        return;
      }
      // Véhicule à l'arrêt (vitesse nulle ou inconnue) : une prolongation en cours s'arrête là
      // où elle est, un glissement en cours finit sur la position SANS prolongation ensuite.
      live.lastFixAt = now;
      let cameraStopped = false;
      if (live.speedMps > 0 && now >= live.start + live.duration) {
        if (live.frame !== null) {
          window.cancelAnimationFrame(live.frame);
          live.frame = null;
        }
        live.from = shown.position;
        live.to = shown.position;
        live.bearingFrom = shown.bearing;
        live.bearingTo = shown.bearing;
        live.start = now;
        live.duration = 0;
        live.panExtrapolated = true;
        live.animating = false;
        live.marker.setLatLng(shown.position);
        if (live.follow) {
          map.stop();
          cameraStopped = true;
        }
      }
      if (liveRules) {
        live.speedMps = 0;
      }
      // 2026-10-14 : et si la caméra vient d'être arrêtée en plein « Recentrer », elle repart
      // vers le repère, là où il s'est arrêté.
      if (followTurnedOn || (recentering && cameraStopped)) {
        this.gpsRecenter(map, live, now);
      }
      return;
    }

    // 2026-10-11 : deux cas où le repère ne glisse pas vers la position reçue :
    // - véhicule à l'arrêt d'après le GPS (vitesse annoncée nulle) et position à moins de 4 m :
    //   le bruit du GPS à l'arrêt ne fait plus errer le repère ;
    // - position reçue un peu DERRIÈRE le repère (moins de 30 m ; le relevé est en retard sur
    //   la prolongation, ou le véhicule s'est arrêté pendant elle) : le repère ne recule pas. Si
    //   le GPS dit que l'on roule, il CONTINUE d'avancer, un peu moins vite et pendant une
    //   cadence et demie au plus (il est déjà devant : pas de longue prolongation), le temps que
    //   le véhicule le rattrape (un arrêt d'une seconde se voyait) ; sinon il attend sur place.
    //   Un vrai recul (plus de 30 m) est suivi.
    const back = distanceM(shown.position, target);
    const stopped = liveRules && speedGiven !== null && speedGiven < 0.5 && back < GPS_STOPPED_HOLD_M;
    let behindClose = false;
    if (liveRules && live.headingKnown) {
      const toTarget = bearingDeg(shown.position, target);
      // Écart d'angle signé entre la direction suivie et celle de la position reçue, dans
      // [-180, 180[ : au-delà de 90° en valeur absolue, la position est derrière.
      const turn = ((toTarget - live.heading + 540) % 360) - 180;
      // 2026-10-12 : sauf si le véhicule a TOURNÉ. À un angle droit de l'itinéraire, quand le
      // repère est arrivé au coin (ou l'a dépassé de peu sur sa lancée), la position suivante
      // est sur le côté, à plus de 90° de l'ancienne direction : elle était prise pour une
      // position « derrière », et le repère continuait tout droit, hors de la route, jusqu'à
      // 30 m d'écart (mesuré : 10 à 13 m au-delà du coin pendant 3 s, puis un rattrapage d'un
      // coup). Un véhicule qui roule et dont le cap annoncé s'écarte nettement de la direction
      // suivie a tourné : le repère glisse vers la position, comme pour toute autre.
      const turned =
        speedGiven !== null &&
        speedGiven >= 0.5 &&
        Number.isFinite(bearingGiven) &&
        Math.abs(angleDelta(live.heading, bearingGiven)) > GPS_TURNED_DEG;
      // Et « derrière » veut dire dans l'axe : à plus de `GPS_BEHIND_MIN_DEG` de la direction
      // suivie. Une position sur le côté (virage doux pris en retard : le cap annoncé a peu
      // changé, mais la position n'est plus sur la ligne suivie) n'est pas un relevé en retard.
      behindClose =
        Math.abs(turn) > GPS_BEHIND_MIN_DEG && back < GPS_BACKWARD_HOLD_M && !turned;
    }
    if (stopped || behindClose) {
      if (live.frame !== null) {
        window.cancelAnimationFrame(live.frame);
        live.frame = null;
      }
      const rolling = behindClose && !stopped && speedGiven !== null && speedGiven >= 0.5;
      live.from = shown.position;
      live.to = shown.position;
      live.bearingFrom = shown.bearing;
      live.bearingTo = bearing;
      live.start = now;
      live.duration = 0;
      live.lastFixAt = now;
      // En roulant : vitesse réduite pour résorber l'avance en deux secondes environ, jamais
      // sous le tiers de la vitesse réelle ; la prolongation repart de la position affichée.
      live.speedMps = rolling ? Math.max(speedGiven / 3, speedGiven - back / 2) : 0;
      if (rolling) {
        live.extrapolateMs = Math.min(extrapolateMs, Math.round(cadenceMs * 1.5));
      }
      live.marker.setLatLng(shown.position);
      if (follow) {
        map.stop();
      }
      if (rolling && live.extrapolateMs > 0) {
        // Suivi en cours (y compris un « Recentrer » qui arrive ici) : à la première image,
        // `gpsFrame` fait rejoindre à la caméra le bout de cette prolongation, en même temps
        // que le repère. C'est ce déplacement qui ramène la caméra sur le repère.
        live.panExtrapolated = false;
        live.animating = true;
        live.steer = true;
        live.frame = window.requestAnimationFrame(this.gpsFrame);
        this.gpsRecenterUntil = 0;
      } else {
        live.panExtrapolated = true;
        live.animating = false;
        // 2026-10-14 : « Recentrer » (le suivi revient) alors que la position reçue est ignorée
        // (arrêt, ou derrière le repère) : la carte rejoint le repère là où il est, en douceur.
        // Avant, elle ne bougeait pas : `live.to` est ici la position affichée, pas la position
        // reçue ; « Recentrer », qui renvoie la même position reçue, retombait donc dans cette
        // branche, et à l'arrêt chaque position suivante aussi. Seule la rotation reprenait.
        // De même quand une position gardée sur place arrive PENDANT ce glissement de caméra
        // (`map.stop()` ci-dessus l'a arrêté en route) : il repart vers le repère.
        if (followTurnedOn || recentering) {
          this.gpsRecenter(map, live, now);
        }
      }
      return;
    }

    const point = map.latLngToContainerPoint(target);
    const mapSize = map.getSize();
    if (point.x < 0 || point.y < 0 || point.x > mapSize.x || point.y > mapSize.y) {
      // Position hors de l'écran (changement de source, longue coupure du GPS, traceur
      // parti loin) : pas de glissement à travers plusieurs écrans, le repère et la carte
      // se posent dessus, comme pour une première position.
      if (live.frame !== null) {
        window.cancelAnimationFrame(live.frame);
        live.frame = null;
      }
      live.from = target;
      live.to = target;
      live.bearingFrom = bearing;
      live.bearingTo = bearing;
      live.start = now;
      live.duration = 0;
      live.lastFixAt = now;
      live.speedMps = 0;
      live.panExtrapolated = true;
      live.animating = false;
      live.rotateTarget = bearing;
      live.marker.setLatLng(target);
      if (live.img) {
        live.img.style.transform = `rotate(${bearing.toFixed(1)}deg)`;
      }
      if (follow) {
        map.setView(target, Math.max(map.getZoom(), this.appZoom), { animate: false });
      }
      return;
    }

    live.from = shown.position;
    live.bearingFrom = shown.bearing;
    live.to = target;
    live.bearingTo = bearing;
    // Durée du glissement : la cadence attendue si l'application la donne (téléphone : le
    // repère rejoint chaque position en une seconde, à l'allure réelle même après un retard),
    // sinon le temps écoulé depuis la position précédente (traceur, positions espacées).
    live.duration =
      cadenceMs > 0
        ? Math.max(GPS_SLIDE_MIN_MS, cadenceMs)
        : Math.min(GPS_SLIDE_MAX_MS, Math.max(GPS_SLIDE_MIN_MS, now - live.lastFixAt));
    live.lastFixAt = now;
    live.start = now;
    // Vitesse et direction pour la prolongation éventuelle : la vitesse du GPS si
    // l'application l'annonce (deux positions très rapprochées donneraient sinon une vitesse
    // de glissement aberrante, donc une prolongation trop loin), sinon celle du glissement.
    const slideM = distanceM(live.from, live.to);
    const slideSpeed = Math.min(GPS_MAX_SPEED_MPS, slideM / (live.duration / 1000));
    live.speedMps = speedGiven !== null ? Math.min(speedGiven, slideSpeed * 1.5) : slideSpeed;
    if (slideM > 0.5) {
      live.heading = bearingDeg(live.from, live.to);
      live.headingKnown = true;
    }
    live.panExtrapolated = false;
    live.animating = true;
    live.steer = slideM >= GPS_ROTATE_MIN_MOVE_M;

    if (follow) {
      if (followTurnedOn || map.getZoom() < this.appZoom) {
        // La carte rejoint d'abord le repère (zoom de l'application au moins), sans animation :
        // le glissement part de là.
        map.setView(live.from, Math.max(map.getZoom(), this.appZoom), { animate: false });
      }
      // Même durée, linéaire : la caméra et le repère arrivent ensemble. Un nouveau relevé
      // pendant le glissement reprend la caméra là où elle est (`PosAnimation.stop` pose la
      // position du moment, puis `run` repart de là), comme le repère (`gpsShown`).
      map.panTo(live.to, {
        animate: true,
        duration: live.duration / 1000,
        easeLinearity: 1,
        noMoveStart: true,
      });
      if (recentering && this.gpsRecenterUntil > 0) {
        // 2026-10-14 : la caméra n'avait pas fini de rejoindre le repère ; elle le rejoint à la
        // fin de ce glissement. Jusque-là, une position gardée sur place la relance (voir plus
        // haut) au lieu de la laisser en route.
        this.gpsRecenterUntil = Math.max(this.gpsRecenterUntil, live.start + live.duration);
      }
    }
    if (live.frame === null) {
      live.frame = window.requestAnimationFrame(this.gpsFrame);
    }
  };

  /**
   * Position et cap affichés à cet instant : le glissement en cours, interpolé ; après sa
   * fin, la prolongation à la même vitesse et dans la même direction (bornée par
   * `extrapolateMs`) ; puis l'arrêt sur la dernière position visée.
   */
  private gpsShown = (now: number): { position: LatLngLiteral; bearing: number } => {
    const live = this.gpsLive;
    if (!live) {
      return { position: { lat: 0, lng: 0 }, bearing: 0 };
    }
    const end = live.start + live.duration;
    if (live.duration > 0 && now < end) {
      const t = Math.max(0, (now - live.start) / live.duration);
      return {
        position: {
          lat: live.from.lat + (live.to.lat - live.from.lat) * t,
          lng: live.from.lng + (live.to.lng - live.from.lng) * t,
        },
        bearing: lerpAngle(live.bearingFrom, live.bearingTo, t),
      };
    }
    const extraMs = Math.min(Math.max(0, now - end), live.extrapolateMs);
    if (extraMs > 0 && live.speedMps > 0) {
      return {
        position: movePoint(live.to, live.heading, (live.speedMps * extraMs) / 1000),
        bearing: live.bearingTo,
      };
    }
    return { position: live.to, bearing: live.bearingTo };
  };

  /** Une image du glissement (ou de la prolongation) : le repère est posé à sa position du moment. */
  private gpsFrame = () => {
    const live = this.gpsLive;
    if (!live) {
      return;
    }
    const now = Date.now();
    live.frame = null;
    try {
      const shown = this.gpsShown(now);
      if (live.animating) {
        live.marker.setLatLng(shown.position);
        if (live.img) {
          live.img.style.transform = `rotate(${shown.bearing.toFixed(1)}deg)`;
        }
        const end = live.start + live.duration;
        if (
          !live.panExtrapolated &&
          now >= end &&
          live.extrapolateMs > 0 &&
          live.speedMps > 0
        ) {
          // Fin du glissement sans position suivante : la caméra continue elle aussi, même
          // vitesse, même direction, pendant au plus la durée de prolongation.
          live.panExtrapolated = true;
          const map = this.state.mapRef && this.state.mapRef.leafletElement;
          if (map && live.follow) {
            map.panTo(
              movePoint(live.to, live.heading, (live.speedMps * live.extrapolateMs) / 1000),
              {
                animate: true,
                duration: live.extrapolateMs / 1000,
                easeLinearity: 1,
                noMoveStart: true,
              }
            );
          }
        }
      }
      // 2026-10-12 : la carte tourne dans la même image que le repère (pas de seconde boucle).
      this.gpsRotate(live, now, shown.bearing);
    } finally {
      const end = live.start + live.duration;
      const extrapolating = live.extrapolateMs > 0 && live.speedMps > 0;
      // Sans glissement ni prolongation (première position, arrêt, position hors écran), une
      // seule image suffit ; une prolongation seule (vitesse réduite après une position reçue
      // derrière le repère) continue jusqu'à son terme.
      const finished = extrapolating
        ? now >= end + live.extrapolateMs
        : live.duration === 0 || now >= end;
      if (finished) {
        live.animating = false;
      }
      // La boucle continue tant que le repère bouge, ou que la carte n'a pas fini de tourner.
      if (live.animating || this.gpsRotationPending(live, now)) {
        live.frame = window.requestAnimationFrame(this.gpsFrame);
      }
    }
  };

  /**
   * 2026-10-12 : carte dans le sens de la marche (champ `headingUp` du message `gpsActive`).
   *
   * Leaflet 1.6 ne sait pas tourner une carte : c'est le navigateur qui tourne le bloc de la
   * carte (`#map-rotor`, voir `MapComponent.view.tsx`), agrandi en carré pour couvrir les coins.
   * Leaflet croit toujours la carte droite : rien ne change dans ses calculs. L'angle suit le cap
   * du repère, image par image dans `gpsFrame`, limité par `GPS_ROTATE_MAX_DEG_S`. Le cap VISÉ
   * ne change que quand le repère avance : à l'arrêt, le cap reçu est ignoré (à la première
   * position et après une position hors de l'écran, la carte rejoint le cap reçu, qui est celui
   * de la route quand le repère est collé à l'itinéraire). Le repère du guidage, tourné de son
   * cap DANS la carte, pointe donc vers le haut ; les autres repères et les infobulles sont
   * contre-tournés par la feuille de style (variable `--map-rotation`).
   *
   * Leaflet lit mal un glissé ou un pincement sur une carte tournée (ses calculs ignorent la
   * rotation). La carte n'est donc tournée que pendant le suivi : dès qu'un doigt se pose sur le
   * fond de carte, elle revient nord en haut, sans animation, AVANT que Leaflet ne traite le
   * geste. Un appui ou un glissé coupent le suivi (c'est l'application qui le dit, `follow:
   * false`) : la carte attend cette annonce, nord en haut ; « Recentrer » (`follow: true`) rend
   * le suivi et la rotation. Après un pincement, qui ne coupe pas le suivi, la rotation reprend
   * à la fin du zoom. Un appui sur un repère (ouvrir une station) ou sur une infobulle (la
   * fermer) laisse la carte tournée ; s'il devient un glissé, elle se redresse à ce moment.
   *
   * Sans `headingUp` dans le message : rien de tout cela n'est posé, la page se comporte comme
   * celle du 2026-10-11.
   */
  private applyHeadingUp = (data: GpsActiveMessage | null) => {
    const live = this.gpsLive;
    if (!live) {
      // Repère retiré : `removeGpsActive` a déjà remis la carte droite.
      return;
    }
    live.headingUp = !!data && data.headingUp === true;
    const followTurnedOn = live.follow && !live.rotateFollow;
    live.rotateFollow = live.follow;
    if (live.headingUp) {
      this.enableRotation();
    }
    if (!this.rotateOn) {
      return;
    }
    if (!live.follow) {
      // Suivi coupé : nord en haut tout de suite.
      this.setMapAngle(0);
    } else if (followTurnedOn) {
      // « Recentrer » : la rotation reprend avec le suivi, sans attendre.
      this.rotateHold = false;
      this.rotatePending = null;
      this.rotateResumeAt = 0;
    }
    if (!live.headingUp && this.mapAngle === 0) {
      // Bouton éteint et carte déjà droite : elle reprend sa taille.
      this.disableRotation();
      return;
    }
    // Filet : un geste fini dont la fin n'a pas été vue (zoom interrompu, doigt sorti de la
    // page) ne garde pas la carte nord en haut.
    if (
      this.rotateHold &&
      !this.rotateDown &&
      (!this.rotateZoomBusy() || Date.now() - this.rotateUpAt > GPS_ROTATE_STUCK_MS)
    ) {
      this.releaseRotation();
    }
    this.gpsKick();
  };

  /** La carte doit-elle être tournée à cet instant ? */
  private rotationWanted = (live: GpsLive, now: number): boolean =>
    this.rotateOn &&
    live.headingUp &&
    live.follow &&
    !this.rotateHold &&
    now >= this.rotateResumeAt;

  /** Reste-t-il de l'angle à parcourir ? */
  private gpsRotationPending = (live: GpsLive, now: number): boolean => {
    if (!this.rotateOn) {
      return false;
    }
    const desired = this.rotationWanted(live, now) ? live.rotateTarget : 0;
    return Math.abs(angleDelta(this.mapAngle, desired)) > 0.001;
  };

  /** Relance la boucle d'images si la carte a de l'angle à parcourir et qu'elle est au repos. */
  private gpsKick = () => {
    const live = this.gpsLive;
    if (live && live.frame === null && this.gpsRotationPending(live, Date.now())) {
      live.frame = window.requestAnimationFrame(this.gpsFrame);
    }
  };

  /** Une image de la rotation : la carte fait un pas vers le cap visé (ou vers le nord). */
  private gpsRotate = (live: GpsLive, now: number, bearing: number) => {
    if (!this.rotateOn) {
      return;
    }
    const wanted = this.rotationWanted(live, now);
    if (wanted && live.animating && live.steer) {
      live.rotateTarget = bearing;
    }
    const desired = wanted ? live.rotateTarget : 0;
    const gap = now - this.rotateFrameAt;
    this.rotateFrameAt = now;
    const delta = angleDelta(this.mapAngle, desired);
    if (Math.abs(delta) > 0.001) {
      // Première image après un repos de la boucle : un pas d'une image, pas celui du repos.
      const dt = gap > 250 || gap < 0 ? 16 : gap;
      const maxStep = (GPS_ROTATE_MAX_DEG_S * dt) / 1000;
      this.setMapAngle(
        Math.abs(delta) <= maxStep ? desired : this.mapAngle + (delta > 0 ? maxStep : -maxStep)
      );
    }
    if (!live.headingUp && this.mapAngle === 0) {
      // Bouton éteint : la carte est revenue au nord, elle reprend sa taille.
      this.disableRotation();
    }
  };

  /** Pose l'angle de la carte (0 = nord en haut) sur le bloc tourné et pour la feuille de style. */
  private setMapAngle = (angle: number) => {
    const normalized = ((angle % 360) + 360) % 360;
    this.mapAngle = normalized < 0.0005 || normalized > 359.9995 ? 0 : normalized;
    const rotor = this.rotor;
    if (!rotor) {
      return;
    }
    rotor.style.transform = this.mapAngle === 0 ? "" : `rotate(${(-this.mapAngle).toFixed(2)}deg)`;
    rotor.style.setProperty("--map-rotation", `${this.mapAngle.toFixed(2)}deg`);
  };

  /** Passe la carte en carré et pose les écouteurs de gestes (début d'un guidage `headingUp`). */
  private enableRotation = () => {
    if (this.rotateOn) {
      return;
    }
    const map = this.state.mapRef && this.state.mapRef.leafletElement;
    const container = document.getElementById("map-container");
    const rotor = document.getElementById("map-rotor");
    if (!map || !container || !rotor) {
      return;
    }
    this.rotateOn = true;
    this.rotor = rotor;
    this.rotateContainer = container;
    this.rotateHold = false;
    this.rotateDown = false;
    this.rotateUpAt = 0;
    this.rotateCutsFollow = false;
    this.rotatePending = null;
    this.rotateResumeAt = 0;
    rotor.classList.add("heading-up");
    container.addEventListener("touchstart", this.onRotateDown, ROTATE_LISTEN);
    container.addEventListener("mousedown", this.onRotateDown, ROTATE_LISTEN);
    window.addEventListener("touchmove", this.onRotateMove, ROTATE_LISTEN);
    window.addEventListener("mousemove", this.onRotateMove, ROTATE_LISTEN);
    window.addEventListener("touchend", this.onRotateUp, ROTATE_LISTEN);
    window.addEventListener("touchcancel", this.onRotateUp, ROTATE_LISTEN);
    window.addEventListener("mouseup", this.onRotateUp, ROTATE_LISTEN);
    map.on("zoomend", this.onRotateZoomEnd);
    map.on("dragstart", this.onRotateCutsFollow);
    map.on("click", this.onRotateCutsFollow);
    this.refreshPopupOrigin();
    this.setState({ rotorSquare: true });
  };

  /** Remet la carte droite, à sa taille, et retire les écouteurs (fin du guidage, bouton éteint). */
  private disableRotation = () => {
    if (!this.rotateOn) {
      return;
    }
    this.rotateOn = false;
    if (this.rotateTimer !== null) {
      window.clearTimeout(this.rotateTimer);
      this.rotateTimer = null;
    }
    const container = this.rotateContainer;
    if (container) {
      container.removeEventListener("touchstart", this.onRotateDown, ROTATE_LISTEN);
      container.removeEventListener("mousedown", this.onRotateDown, ROTATE_LISTEN);
    }
    window.removeEventListener("touchmove", this.onRotateMove, ROTATE_LISTEN);
    window.removeEventListener("mousemove", this.onRotateMove, ROTATE_LISTEN);
    window.removeEventListener("touchend", this.onRotateUp, ROTATE_LISTEN);
    window.removeEventListener("touchcancel", this.onRotateUp, ROTATE_LISTEN);
    window.removeEventListener("mouseup", this.onRotateUp, ROTATE_LISTEN);
    const map = this.state.mapRef && this.state.mapRef.leafletElement;
    if (map) {
      map.off("zoomend", this.onRotateZoomEnd);
      map.off("dragstart", this.onRotateCutsFollow);
      map.off("click", this.onRotateCutsFollow);
    }
    this.setMapAngle(0);
    if (this.rotor) {
      this.rotor.classList.remove("heading-up");
      this.rotor.style.removeProperty("--map-rotation");
    }
    this.rotor = null;
    this.rotateContainer = null;
    this.rotateHold = false;
    this.rotateDown = false;
    this.rotateUpAt = 0;
    this.rotateCutsFollow = false;
    this.rotatePending = null;
    this.rotateResumeAt = 0;
    if (!this.unmounting) {
      this.setState({ rotorSquare: false });
    }
  };

  /** Un geste tient la carte nord en haut : immédiatement, sans animation. */
  private holdRotation = () => {
    this.rotatePending = null;
    this.rotateHold = true;
    if (this.rotateTimer !== null) {
      window.clearTimeout(this.rotateTimer);
      this.rotateTimer = null;
    }
    if (this.mapAngle !== 0) {
      this.setMapAngle(0);
      // Si un doigt était déjà posé (sur un repère), le glissé de Leaflet a relevé l'échelle
      // de la carte à cet appui, sur son rectangle englobant de carte tournée (plus grand
      // qu'elle) : remise à 1, la carte est droite. Sans doigt posé avant, Leaflet la relève
      // lui-même juste après.
      const map = this.state.mapRef && this.state.mapRef.leafletElement;
      const draggable = map && map.dragging && map.dragging._draggable;
      if (draggable && draggable._parentScale) {
        draggable._parentScale.x = 1;
        draggable._parentScale.y = 1;
      }
    }
  };

  /**
   * Fin du geste : la rotation pourra reprendre si le suivi tient. Après un geste qui coupe le
   * suivi dans l'application, la carte attend son annonce (`follow: false`) ; sinon (pincement)
   * elle reprend après `GPS_ROTATE_RESUME_MS`.
   */
  private releaseRotation = () => {
    if (!this.rotateHold) {
      return;
    }
    this.rotateHold = false;
    this.deferRotation(this.rotateCutsFollow ? GPS_ROTATE_FOLLOW_WAIT_MS : GPS_ROTATE_RESUME_MS);
  };

  /** Pas de rotation avant `ms` millisecondes ; la boucle est relancée à l'échéance. */
  private deferRotation = (ms: number) => {
    const now = Date.now();
    this.rotateResumeAt = Math.max(this.rotateResumeAt, now + ms);
    if (this.rotateTimer !== null) {
      window.clearTimeout(this.rotateTimer);
    }
    this.rotateTimer = window.setTimeout(() => {
      this.rotateTimer = null;
      this.gpsKick();
    }, this.rotateResumeAt - now + 20);
  };

  /** Leaflet est-il en plein zoom (pincement en cours, ou animation de zoom) ? */
  private rotateZoomBusy = (): boolean => {
    const map = this.state.mapRef && this.state.mapRef.leafletElement;
    return !!(map && (map._animatingZoom || (map.touchZoom && map.touchZoom._zooming)));
  };

  /**
   * Doigt (ou souris) posé sur la carte, vu AVANT Leaflet. Les événements de souris que Leaflet
   * fabrique lui-même à partir d'un appui (`_simulated`) sont ignorés.
   */
  private onRotateDown = (event: any) => {
    if (event._simulated) {
      return;
    }
    const touches = event.touches;
    const first = touches ? touches[0] : event;
    if (!first) {
      return;
    }
    const target = event.target as Element | null;
    const within = (selector: string) => !!(target && target.closest && target.closest(selector));
    const second = !!touches && touches.length > 1;
    if (!second && within(".leaflet-popup-content-wrapper")) {
      // Cadre d'une infobulle : Leaflet arrête là la propagation, ce geste ne déplace jamais
      // la carte. Rien à redresser.
      return;
    }
    if (!second) {
      this.rotateCutsFollow = false;
    }
    this.rotateDown = true;
    if (second || !within(".leaflet-marker-icon, .leaflet-popup")) {
      this.holdRotation();
      return;
    }
    if (!this.rotateHold) {
      this.rotatePending = { x: first.clientX, y: first.clientY };
    }
  };

  /** Un appui commencé sur un repère devient un glissé (ou un pincement) : la carte se redresse. */
  private onRotateMove = (event: any) => {
    const start = this.rotatePending;
    if (!start || event._simulated) {
      return;
    }
    const touches = event.touches;
    const first = touches ? touches[0] : event;
    if (!first) {
      return;
    }
    const moved = Math.abs(first.clientX - start.x) + Math.abs(first.clientY - start.y);
    if ((touches && touches.length > 1) || moved >= GPS_ROTATE_DRAG_PX) {
      this.holdRotation();
    }
  };

  /** Dernier doigt levé : la rotation peut reprendre (après la fin du zoom s'il y en a un). */
  private onRotateUp = (event: any) => {
    if (event._simulated) {
      return;
    }
    if (event.touches && event.touches.length > 0) {
      return;
    }
    this.rotateDown = false;
    this.rotateUpAt = Date.now();
    this.rotatePending = null;
    if (this.rotateHold && !this.rotateZoomBusy()) {
      this.releaseRotation();
    }
  };

  /** Fin d'un zoom : si le geste est fini, la rotation peut reprendre. */
  private onRotateZoomEnd = () => {
    if (this.rotateHold && !this.rotateDown && !this.rotateZoomBusy()) {
      this.releaseRotation();
    }
  };

  /**
   * Leaflet commence un glissé, ou vient de lire un appui sur le fond de carte : dans les deux
   * cas l'application va couper le suivi. La carte reste nord en haut en attendant son annonce
   * (l'appui est lu par Leaflet APRÈS le lever du doigt : l'attente est rallongée ici).
   */
  private onRotateCutsFollow = () => {
    this.rotateCutsFollow = true;
    if (!this.rotateHold) {
      this.deferRotation(GPS_ROTATE_FOLLOW_WAIT_MS);
    }
  };

  /**
   * La carte a changé de taille ou de forme (fenêtre redimensionnée, passage au carré et
   * retour). Leaflet garde sa taille en mémoire et ne la relit pas seul dans ce cas : sans cet
   * appel il croyait encore la carte haute de 826 px quand la zone visible en faisait 740 (la
   * barre d'onglets venue après le premier dessin), et posait le repère 43 px sous le milieu.
   * Le point au centre de la carte reste au centre de la zone visible ; pendant un suivi, la
   * caméra reprend ensuite sa route vers le repère.
   */
  private onRotorLayout = () => {
    const map = this.state.mapRef && this.state.mapRef.leafletElement;
    if (!map) {
      return;
    }
    const element: HTMLElement = map.getContainer();
    const before = map.getSize();
    if (element.clientWidth === before.x && element.clientHeight === before.y) {
      return;
    }
    // Un déplacement animé de la caméra repose la carte sur sa trajectoire à chaque image, et
    // une dernière fois à son arrêt : il déferait le décalage que pose `invalidateSize` (la
    // carte sautait de la moitié de l'écart de taille, puis revenait). Arrêté d'abord.
    if (map._panAnim) {
      map._panAnim.stop();
    }
    map.invalidateSize({ pan: true, animate: false });
    const live = this.gpsLive;
    if (live && live.follow) {
      this.gpsResumeFollow();
    }
  };

  /**
   * Une infobulle s'ouvre. Leaflet déplace la carte pour qu'elle tienne dans la carte ; quand
   * la carte est carrée, plus grande que l'écran, ses marges sont agrandies de ce qui dépasse
   * de la zone visible (sinon une infobulle près d'un bord de l'écran resterait coupée). Carte
   * tournée, donc suivi en cours : pas de déplacement, la caméra suit le repère.
   */
  private onPopupOpen = (event: any) => {
    const popup = event && event.popup;
    const map = this.state.mapRef && this.state.mapRef.leafletElement;
    if (popup && popup.options && map) {
      const container = this.rotateContainer;
      if (this.rotateOn && container) {
        const size = map.getSize();
        const padding = L.point(
          Math.max(0, (size.x - container.clientWidth) / 2) + 5,
          Math.max(0, (size.y - container.clientHeight) / 2) + 5
        );
        popup.options.autoPanPaddingTopLeft = padding;
        popup.options.autoPanPaddingBottomRight = padding;
        popup.options.autoPan = this.mapAngle === 0;
      } else {
        // Réglages de Leaflet par défaut (les trois ne sont posés que par les lignes ci-dessus).
        delete popup.options.autoPanPaddingTopLeft;
        delete popup.options.autoPanPaddingBottomRight;
        delete popup.options.autoPan;
      }
    }
    // Le contenu de l'infobulle est posé juste après : point de rotation à l'image suivante.
    window.requestAnimationFrame(this.refreshPopupOrigin);
  };

  /**
   * Point autour duquel l'infobulle ouverte est contre-tournée quand la carte tourne : le point
   * de la carte qu'elle désigne (le repère), pour qu'elle reste au-dessus de lui à l'écran.
   * Leaflet pose sa propre transformation sur l'infobulle : ce sont ses trois parties (cadre,
   * pointe, croix) que la feuille de style tourne, chacune autour de ce même point.
   */
  private refreshPopupOrigin = () => {
    const map = this.state.mapRef && this.state.mapRef.leafletElement;
    const popup = map && map._popup;
    const container: HTMLElement | null = popup && popup._container;
    if (!container || !container.parentNode) {
      return;
    }
    const offset = L.point(popup.options.offset || [0, 7]);
    const marginBottom = parseFloat(window.getComputedStyle(container).marginBottom) || 0;
    const x = Math.round(container.offsetWidth / 2) - offset.x;
    const y = container.offsetHeight + marginBottom - offset.y;
    for (let i = 0; i < container.children.length; i++) {
      const part = container.children[i] as HTMLElement;
      part.style.transformOrigin = `${x - part.offsetLeft}px ${y - part.offsetTop}px`;
    }
  };

  /**
   * Un zoom au doigt pendant le suivi arrête l'animation de la caméra (Leaflet arrête tout
   * déplacement en cours dès que le geste commence) ; le repère, lui, continue de glisser. À la
   * fin du zoom, la caméra reprend sa route vers la position visée, dans le temps qu'il reste,
   * au lieu d'attendre la position suivante.
   */
  private gpsResumeFollow = () => {
    const live = this.gpsLive;
    const map = this.state.mapRef && this.state.mapRef.leafletElement;
    if (!live || !map || !live.follow) {
      return;
    }
    const now = Date.now();
    const remaining = live.start + live.duration - now;
    if (remaining < 50) {
      // Pas de glissement en cours : la carte se pose sur le repère (à sa position du
      // moment, prolongation comprise). Couvre aussi le cas d'un zoom animé qui a fait
      // ignorer le `setView` de la première position (Leaflet tient un `setView` pour fait
      // tant qu'un zoom animé est en cours).
      map.setView(this.gpsShown(now).position, Math.max(map.getZoom(), this.appZoom), {
        animate: false,
      });
      return;
    }
    map.panTo(live.to, {
      animate: true,
      duration: remaining / 1000,
      easeLinearity: 1,
      noMoveStart: true,
    });
  };

  /**
   * « Recentrer » : la carte rejoint le repère, au zoom de l'application au moins. Pendant un
   * glissement, elle vise la position d'arrivée dans le temps qu'il reste (le repère continue
   * d'avancer) ; sinon la position affichée, en une demi-seconde.
   */
  private gpsRecenter = (map: any, live: GpsLive, now: number) => {
    const remaining = live.start + live.duration - now;
    const sliding = remaining >= 50;
    const position = sliding ? live.to : this.gpsShown(now).position;
    if (map.getZoom() < this.appZoom) {
      map.setView(position, this.appZoom, { animate: false });
      this.gpsRecenterUntil = 0;
      return;
    }
    map.panTo(position, {
      animate: true,
      duration: sliding ? remaining / 1000 : GPS_RECENTER_S,
      easeLinearity: sliding ? 1 : undefined,
      noMoveStart: true,
    });
    // 2026-10-14 : noté APRÈS le départ du glissement (voir `gpsRecenterUntil`).
    this.gpsRecenterUntil = now + (sliding ? remaining : GPS_RECENTER_S * 1000);
  };

  /** 2026-10-14 : un geste déplace la carte (`movestart`) : plus de « Recentrer » en cours. */
  private gpsRecenterCancel = () => {
    this.gpsRecenterUntil = 0;
  };

  private createGpsMarker = (
    position: LatLngLiteral,
    bearing: number,
    icon: string,
    size: [number, number]
  ): L.Marker => {
    const image = icon
      ? `<img src="${icon}" alt="" style="width:${size[0]}px;height:${size[1]}px;transform:rotate(${bearing.toFixed(1)}deg);transform-origin:center center;">`
      : "";
    return L.marker(position, {
      icon: L.divIcon({
        className: "gps-live-marker",
        html: image,
        iconSize: size,
        iconAnchor: [size[0] / 2, size[1] / 2],
      }),
      interactive: false,
      keyboard: false,
      zIndexOffset: 1000,
    } as any);
  };

  /** Fin du guidage : le repère est retiré, la carte revient droite et à sa taille. */
  private removeGpsActive = () => {
    this.removeGpsMarker();
    this.disableRotation();
  };

  private removeGpsMarker = () => {
    const live = this.gpsLive;
    if (!live) {
      return;
    }
    if (live.frame !== null) {
      window.cancelAnimationFrame(live.frame);
    }
    const map = this.state.mapRef && this.state.mapRef.leafletElement;
    if (map) {
      map.off("zoomend", this.gpsResumeFollow);
      map.off("movestart", this.gpsRecenterCancel);
    }
    live.marker.remove();
    this.gpsLive = null;
    this.gpsRecenterUntil = 0;
  };

  private onMapEvent = (
    webViewLeafletEvent: WebViewLeafletEvents,
    payload?: WebviewLeafletMessagePayload
  ) => {
    if (
      webViewLeafletEvent === WebViewLeafletEvents.ON_MOVE ||
      webViewLeafletEvent === WebViewLeafletEvents.ON_MOVE_END ||
      webViewLeafletEvent === WebViewLeafletEvents.ON_ZOOM
    ) {
      // 2026-10-10 : au plus un envoi par délai (voir `MOVE_EVENTS_MIN_INTERVAL_MS`).
      const now = Date.now();
      const last = this.lastMoveEventAt[webViewLeafletEvent] || 0;
      if (now - last < MOVE_EVENTS_MIN_INTERVAL_MS) {
        return;
      }
      this.lastMoveEventAt[webViewLeafletEvent] = now;
    }
    if (!payload && this.state.mapRef?.leafletElement) {
      const mapCenterPosition: LatLng = {
        lat: this.state.mapRef.leafletElement?.getCenter().lat,
        lng: this.state.mapRef.leafletElement?.getCenter().lng,
      };

      payload = {
        mapCenterPosition: mapCenterPosition,
        bounds: this.state.mapRef.leafletElement?.getBounds(),
        zoom: this.state.mapRef.leafletElement?.getZoom(),
      };
    }
    this.sendMessage({ event: webViewLeafletEvent, payload });
  };

  private setMapRef = (mapRef: any) => {
    if (!this.state.mapRef) {
      this.setState({ mapRef });
    }
  };


  render() {
    console.log('center', this.state.mapCenterPosition)
    return (
      <MapComponentView
        addDebugMessage={this.addDebugMessage}
        onMapEvent={this.onMapEvent}
        setMapRef={this.setMapRef}
        onRotorLayout={this.onRotorLayout}
        {...this.state}
      />
    );
  }
}
